// CPI-1226 follow-up (PO, 5 Sep): picking one of the tenant's existing cancellation reasons
// pre-selects the category. Matching is on the DISPLAYED NAME, not the id - the list is
// admin-editable per tenant, so an id is no more stable than a name. Nothing here is enforced.

import { isVietnamCompany } from './vietnam';

export const CANCELLATION_CATEGORY_BUYER = 'buyer';
export const CANCELLATION_CATEGORY_ERROR = 'error';

const normalise = value => String(value == null ? '' : value).toLowerCase();

// "Cancelled by Developer" -> buyer and "Change of Unit" -> buyer; for the latter both words are
// required, so a "Change of Parcel" row stays unmapped.
//
// CPI-1226 post-fix (PO, 8 Sep): a developer cancellation used to pre-select "due to Error". It
// now pre-selects "due to Buyer", because Error means "this reservation should never have
// existed" - a staff mistake being tidied up - and a developer withdrawing a unit is a real
// opportunity that ended, which belongs under Closed. Nothing maps to Error by name any more;
// Error is reachable only by picking the synthesized "Cancellation due to Error" option below.
export const cancellationCategoryForReasonName = (reasonName) => {
  const name = normalise(reasonName);
  if (!name) return '';
  if (name.includes('developer')) return CANCELLATION_CATEGORY_BUYER;
  if (name.includes('change') && name.includes('unit')) return CANCELLATION_CATEGORY_BUYER;
  return '';
};

// The form holds the reason's id, so resolve it against the list first; an unknown id maps to ''.
export const cancellationCategoryForReasonID = (reasons, reasonID) => {
  const reason = [].concat(reasons || []).find(item => item && item.id === reasonID);
  return reason ? cancellationCategoryForReasonName(reason.name) : '';
};

// CPI-1226 post-fix (PO, 6 Sep 09:32): "change of unit can tie it to due to buyer. by developer
// leave it as it is. so total in drop down will have 3 only. 1 existing and 2 new."
//
// The three entries a Vietnamese cancellation dropdown offers are the two CATEGORIES themselves
// plus the tenant's existing "Cancelled by Developer" row. The categories are synthesized by this
// app, NOT seeded as cancellation_reasons rows: that table is global to a deployment (id, name,
// short_name, description - no company and no country column), so a row added for Vietnam would
// appear in every Malaysian tenant's Settings > Cancellation Reasons screen, and a row inserted by
// a migration would also skip the Credit Control mirror that only the API's create path writes.
//
// A synthesized option carries its category in its own value, so picking one sets
// sr_cancellation_type directly instead of matching words in a name.
const CATEGORY_OPTION_PREFIX = 'category:';

export const cancellationCategoryOptionValue = category => `${CATEGORY_OPTION_PREFIX}${category}`;

const isCategoryOption = choice =>
  typeof choice === 'string' && choice.indexOf(CATEGORY_OPTION_PREFIX) === 0;

// The category a dropdown choice means: read off a synthesized option, or looked up from the name
// of a master-list row. '' when neither, so the caller leaves the category as it was.
export const cancellationCategoryForChoice = (reasons, choice) => (
  isCategoryOption(choice) ?
    choice.slice(CATEGORY_OPTION_PREFIX.length) :
    cancellationCategoryForReasonID(reasons, choice)
);

// The master-list row a dropdown choice refers to, if any. A synthesized option refers to none, so
// nothing is sent as the reason id for it.
export const cancellationReasonIDForChoice = choice => (isCategoryOption(choice) ? '' : choice);

// Which rows of the master list the dropdown may offer. Vietnam no longer offers the legacy
// "Cancelled by Developer" row in the booking cancellation modal. The two synthesized category
// choices remain available, and the master-list rows are not used for Vietnam at all.
//
// "Change of Unit" is folded into "Cancellation due to Buyer"; its buyer mapping above stays,
// because a booking cancelled before this change still carries that reason.
//
// Outside Vietnam this is the identity, so Malaysia offers the list it always offered.
export const visibleCancellationReasons = (auth, reasons) => {
  const list = [].concat(reasons || []).filter(reason => reason && reason.id);
  if (!isVietnamCompany(auth)) {
    return list;
  }
  return [];
};



// WEBPACK FOOTER //
// ./src/utils/cancellationReasonCategory.js